import React from "react";
import { Box, Button, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  SECTION_LINKS,
  fmtMoney,
  fmtUnits,
  laneSummary,
  sectionSubtitle,
} from "../../utils/commandCenterPageDefaults";
import { eyebrowSx } from "../purchase-order-analysis/parts";
import AttentionItem from "./AttentionItem";
import { SECTION_ICONS, sectionTone } from "./sectionStyle";

// Each section opens on its biggest items; the rest are one click away.
export const VISIBLE_PER_SECTION = 5;

const hiddenLabel = (signal, hidden) => {
  const count = hidden.length;
  if (signal === "orders") return `Show ${count} more order group${count === 1 ? "" : "s"}`;
  const summary = laneSummary(hidden);
  const noun = signal === "po" ? "PO" : "product";
  const money = signal === "stock" && summary.hasWindowLoss ? fmtMoney(summary.lostInWindow) : `${fmtMoney(summary.perDay)}/day`;
  return `Show ${count} more ${noun}${count === 1 ? "" : "s"} (${money})`;
};

/**
 * One section of the Command Center: a prominent header that states what the
 * section adds up to, the items as cards (biggest first), and what was
 * tracked but left out of the list.
 */
const Section = ({
  ui,
  id,
  signal,
  label,
  items,
  folded,
  snapshot,
  expanded,
  onExpand,
  highlightedKey,
  relatedTitles,
  onOpenLink,
  onJumpTo,
  onNavigate,
}) => {
  const { colors } = ui;
  const Icon = SECTION_ICONS[signal];
  const summary = laneSummary(items);
  const { fg, bg } = sectionTone(ui, summary);
  const visible = expanded ? items : items.slice(0, VISIBLE_PER_SECTION);
  const hidden = items.slice(visible.length);
  const link = SECTION_LINKS[signal];

  return (
    <Box id={id} component="section" aria-label={label} sx={{ ...ui.card, p: { xs: "16px", md: "20px 22px" }, scrollMarginTop: 16 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, flexWrap: "wrap", mb: items.length ? 2 : 0 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: "11px", display: "grid", placeItems: "center", backgroundColor: bg, flexShrink: 0 }}>
          <Icon sx={{ fontSize: 22, color: fg }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography component="h2" sx={{ fontSize: 17, fontWeight: 700, color: colors.textPrimary }}>
              {label}
            </Typography>
            {summary.count ? (
              <Box component="span" sx={{ fontSize: 12, fontWeight: 700, color: "#fff", backgroundColor: fg, borderRadius: "10px", px: 0.9, lineHeight: "20px" }}>
                {fmtUnits(summary.count)}
              </Box>
            ) : null}
          </Box>
          <Typography sx={{ fontSize: 13, color: colors.textSecondary, mt: 0.4, lineHeight: 1.5 }}>
            {sectionSubtitle(signal, summary, snapshot)}
          </Typography>
        </Box>
        <Button
          size="small"
          onClick={() => onNavigate(link.path)}
          endIcon={<ArrowForwardIcon sx={{ fontSize: 15 }} />}
          sx={{ textTransform: "none", fontSize: 13, color: ui.accent, flexShrink: 0 }}
        >
          Open {link.label}
        </Button>
      </Box>

      {items.length ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {visible.map((item) => (
            <AttentionItem
              key={item.key}
              ui={ui}
              item={item}
              relatedTitles={relatedTitles}
              highlighted={item.key === highlightedKey}
              onOpenLink={onOpenLink}
              onJumpTo={onJumpTo}
            />
          ))}
          {hidden.length ? (
            <Button
              onClick={onExpand}
              sx={{ alignSelf: "flex-start", textTransform: "none", fontSize: 13, fontWeight: 600, color: ui.accent }}
            >
              {hiddenLabel(signal, hidden)}
            </Button>
          ) : null}
        </Box>
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5, color: ui.positive }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
          <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>All good here.</Typography>
        </Box>
      )}

      {folded.length ? (
        <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${colors.border}` }}>
          <Typography sx={{ ...eyebrowSx(colors), mb: 0.75 }}>Also tracked, not listed</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.25, display: "flex", flexDirection: "column", gap: 0.4 }}>
            {folded.map((line) => (
              <Box component="li" key={line.key} sx={{ fontSize: 12.5, color: colors.textSecondary }}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => onNavigate(line.path)}
                  sx={{ border: 0, background: "none", p: 0, font: "inherit", color: "inherit", textAlign: "left", cursor: "pointer", "&:hover": { color: ui.accent, textDecoration: "underline" } }}
                >
                  {line.text}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default Section;
